/**
 * ThemeControls — the shared "Theme" settings block (accent / tint / background
 * hue pickers + 3D-background brightness + interactive-visual picker).
 *
 * Reads/writes the global themeStore, so it's editor-agnostic — used by both the
 * Flow settings (UI tab) and the Synth VR settings (UI tab) so they stay 1:1.
 */

import React, { useRef } from 'react';
import { useTheme, THEME_PRESETS, type BgVisual, type BgVisualSource } from './themeStore';
import { color, segmentBtn } from './theme';
import FlowSlider from '../../editors/flow/FlowSlider';

const swatchBtnStyle = (active: boolean): React.CSSProperties => ({
  ...segmentBtn(active),
  display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-start',
  padding: '8px 12px',
});
const swatchDotStyle = (c: string): React.CSSProperties => ({
  width: 13, height: 13, borderRadius: 99, background: c,
  boxShadow: `0 0 8px ${c}`, flexShrink: 0,
});

const hintStyle: React.CSSProperties = {
  fontSize: 10, color: color.inkFaint, lineHeight: 1.5, margin: 0,
};

function PresetSwatch({ label, hue, c = 1, active, onPick }: { label: string; hue: number; c?: number; active: boolean; onPick: () => void }) {
  // Bright dot for every picker (tint / accent / background) so the background
  // swatches read just as vividly as the tint ones. White preset → near-white.
  const dot = c <= 0 ? 'oklch(0.95 0 0)' : `oklch(0.72 ${0.2 * c} ${hue})`;
  return (
    <button onClick={onPick} style={swatchBtnStyle(active)}>
      <span style={swatchDotStyle(dot)} />
      {label}
    </button>
  );
}

function CustomSwatch({ active, hex, onPick }: { active: boolean; hex: string; onPick: (hex: string) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <button onClick={() => inputRef.current?.click()} style={swatchBtnStyle(active)}>
      <span style={swatchDotStyle(active ? hex : 'conic-gradient(from 0deg, red, yellow, lime, cyan, blue, magenta, red)')} />
      {active ? 'Custom' : 'Custom…'}
      <input
        ref={inputRef}
        type="color"
        value={hex}
        onChange={e => onPick(e.target.value)}
        onClick={e => e.stopPropagation()}
        style={{ position: 'absolute', width: 0, height: 0, opacity: 0, pointerEvents: 'none' }}
        aria-label="Pick a custom colour"
      />
    </button>
  );
}

type ThemeKind = 'accent' | 'tint' | 'background';

function ThemePicker({ kind }: { kind: ThemeKind }) {
  const choice    = useTheme(s => (kind === 'accent' ? s.accent : kind === 'tint' ? s.tint : s.bg));
  const hex       = useTheme(s => (kind === 'accent' ? s.accentHex : kind === 'tint' ? s.tintHex : s.bgHex));
  const setPreset = useTheme(s => (kind === 'accent' ? s.setAccent : kind === 'tint' ? s.setTint : s.setBg));
  const setHex    = useTheme(s => (kind === 'accent' ? s.setAccentHex : kind === 'tint' ? s.setTintHex : s.setBgHex));
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
      {THEME_PRESETS.map(p => (
        <PresetSwatch key={p.id} label={p.label} hue={p.hue} c={p.c ?? 1} active={choice === p.id} onPick={() => setPreset(p.id)} />
      ))}
      <CustomSwatch active={choice === 'custom'} hex={hex} onPick={setHex} />
    </div>
  );
}

function BgBrightnessSlider() {
  const bgBrightness    = useTheme(s => s.bgBrightness);
  const setBgBrightness = useTheme(s => s.setBgBrightness);
  return (
    <div style={{ marginTop: 8 }}>
      <FlowSlider
        label="Background Brightness"
        value={bgBrightness}
        min={0.3} max={2.0} step={0.05}
        onChange={setBgBrightness}
        format={v => `${Math.round(v * 100)}%`}
      />
    </div>
  );
}

function AccentLightnessSlider() {
  const vIcon = useTheme(s => s.accentL);
  const setIcon = useTheme(s => s.setAccentL);
  const vText = useTheme(s => s.textL);
  const setText = useTheme(s => s.setTextL);
  const v2 = useTheme(s => s.accentStructL);
  const set2 = useTheme(s => s.setAccentStructL);
  return (
    <>
      <div style={{ marginTop: 6 }}>
        {/* 0% = accent-tinted, 100% = pure white. */}
        <FlowSlider label="Text" value={vText} min={0} max={1} step={0.02}
          onChange={setText} format={x => `${Math.round(x * 100)}%`} />
      </div>
      <div style={{ marginTop: 6 }}>
        <FlowSlider label="Icons" value={vIcon} min={0.6} max={1.25} step={0.02}
          onChange={setIcon} format={x => `${Math.round(x * 100)}%`} />
      </div>
      <div style={{ marginTop: 6 }}>
        <FlowSlider label="Borders & Fills" value={v2} min={0.6} max={1.25} step={0.02}
          onChange={set2} format={x => `${Math.round(x * 100)}%`} />
      </div>
    </>
  );
}

function TintLightnessSlider() {
  const v = useTheme(s => s.tintL);
  const set = useTheme(s => s.setTintL);
  return (
    <div style={{ marginTop: 6 }}>
      <FlowSlider label="Tint Brightness" value={v} min={0.5} max={1.8} step={0.02}
        onChange={set} format={x => `${Math.round(x * 100)}%`} />
    </div>
  );
}

const VISUAL_OPTIONS: { id: BgVisual; label: string }[] = [
  { id: 'none',   label: 'Off' },
  { id: 'aurora', label: 'Aurora' },
  { id: 'embers', label: 'Embers' },
  { id: 'ripple', label: 'Ripple' },
];

function BgVisualPicker() {
  const visual    = useTheme(s => s.bgVisual);
  const source    = useTheme(s => s.bgVisualSource);
  const setVisual = useTheme(s => s.setBgVisual);
  const setSource = useTheme(s => s.setBgVisualSource);
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
        {VISUAL_OPTIONS.map(o => (
          <button key={o.id} onClick={() => setVisual(o.id)} style={swatchBtnStyle(visual === o.id)}>
            {o.label}
          </button>
        ))}
      </div>
      {visual !== 'none' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6, marginTop: 6 }}>
          {(['map', 'music'] as BgVisualSource[]).map(s => (
            <button key={s} onClick={() => setSource(s)} style={swatchBtnStyle(source === s)}>
              {s === 'map' ? 'React to Map' : 'React to Music'}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** The full Theme block. Drop inside a settings section. */
export default function ThemeControls() {
  return (
    <>
      <div style={{ ...hintStyle, marginBottom: 5, letterSpacing: 0.6 }}>
        ACCENT · highlights &amp; active states
      </div>
      <ThemePicker kind="accent" />
      <AccentLightnessSlider />
      <div style={{ ...hintStyle, margin: '14px 0 5px', letterSpacing: 0.6 }}>
        TINT · background tone (surfaces, waveform)
      </div>
      <ThemePicker kind="tint" />
      <TintLightnessSlider />
      <div style={{ ...hintStyle, margin: '14px 0 5px', letterSpacing: 0.6 }}>
        BACKGROUND · the 3D backdrop
      </div>
      <ThemePicker kind="background" />
      <BgBrightnessSlider />
      <div style={{ ...hintStyle, margin: '14px 0 5px', letterSpacing: 0.6 }}>
        INTERACTIVE VISUALS · particles behind the scene
      </div>
      <BgVisualPicker />
      <div style={{ ...hintStyle, marginTop: 8 }}>
        Three hues drive the whole app. Pick <strong>Custom…</strong> for any colour.
        Visuals pulse with note hits (Map) or the song&apos;s energy (Music).
      </div>
    </>
  );
}
